import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress, Grid } from '@material-ui/core'
import { useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { COLORS } from 'src/utils/Themes/colors'
import { FETCH_RISK_REPORT } from 'src/services/riskCategoriesService'
import documentDownload from 'src/utils/commonMethods/documentDownload'
import { getAgeChartData, getChartData } from 'src/utils/commonMethods'
import { INSURANCE_NAMES } from 'src/utils/constants/InsuranceNames'
import {
  DOWNLOAD_DOCUMENT_ICONS,
  FIRE_INSURANCE_CATEGORIES,
  INSURANCE_CATEGORIES,
  MARINE_INSURANCE_CATEGORIES,
  OTHER_INSURANCE_CATEGORIES,
  PENDING_CLAIM_TYPES,
  TEMPLATE_FEATURES,
} from 'src/utils/constants/common'
import GraphQlErrors from 'src/components/common/GraphQlErrors'
import CloseButton from 'src/components/common/CloseButton'
import NavigateBackButton from 'src/components/common/NavigateBackButton'
import DownloadReports from 'src/components/RiskCategories/components/DownloadReports'
import CallToAction from 'src/components/RiskCategories/components/CallToAction'
import KeySuggestions from 'src/components/RiskCategories/components/KeySuggestions'
import UpcomingRenewals from 'src/components/RiskReports/UpComingRenewals/UpcomingRenewals'
import DynamicKeyPoints from 'src/components/RiskCategories/components/DynamicKeyPoints'
import TotalVehicles from 'src/components/RiskCategories/components/TotalVehicles'
import AgeGraph from 'src/components/RiskReports/AgeGraph/AgeGraph'
import TrendAnalysis from 'src/components/RiskCategories/components/TrendAnalysis'
import Coverages from 'src/components/RiskCategories/components/Coverages'
import { Aggregate } from 'src/components/RiskReports/Aggregate/Aggregate'
import { INSURANCE_DETAILS } from 'src/utils/constants/InsuranceDetails'
import {
  CALL_TO_ACTION_TEXT,
  CATEGORIES_AGE,
  DOWNLOAD_REPORTS_TEXT,
  KEY_SUGGESTIONS,
  TREND_ANALYSIS_TEXT,
} from 'src/utils/constants/RiskCategory'
import PremiumAndClaimsView from 'src/components/RiskReports/PremuimAndClaims/PremiumAndClaimsView'
import PendingClaims from 'src/components/RiskReports/PendingClaims/PendingClaims'

const DynamicInsuranceTemplate = ({
  handleCloseDetails,
  handleTotalCloseDetails,
  insuranceName,
  riskTitle,
  subInsuranceType,
}) => {
  const classes = useStyles()
  const userInfo = useSelector((state) => state?.userInfo)
  const [insuranceDetails, setInsuranceDetails] = useState({})
  const [pendingClaimsGraphSeries, setPendingClaimsGraphSeries] = useState([
    {
      name: '',
      data: [0, 0, 0],
    },
  ])
  const [
    financialPendingClaimsGraphSeries,
    setFinancialPendingClaimsGraphSeries,
  ] = useState([
    {
      name: '',
      data: [0, 0, 0],
    },
  ])
  const [ageGraphSeries, setAgeGraphSeries] = useState([
    {
      name: '',
      data: [0, 0, 0, 0, 0, 0],
    },
  ])
  let loggedInUserClientId = userInfo?.userInfo?.userData?.clientId

  // Api Calls
  const { loading, error, data } = useQuery(FETCH_RISK_REPORT(), {
    variables: {
      clientId: loggedInUserClientId ?? userInfo?.userName,
      insuranceType: insuranceName,
      subInsuranceType: subInsuranceType ?? null,
    },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (data) {
      setInsuranceDetails(data?.getRiskReport)
      const chartData = get(
        data,
        'getRiskReport.riskInsurance.riskData.premiumClaimYearlyData',
        []
      )

      const financialChartData = get(
        data,
        'getRiskReport.riskInsurance.riskData.financialYearlyClaimData',
        []
      )

      const ageChartData = get(
        data,
        'getRiskReport.riskInsurance.riskData.ageDistribution',
        []
      )

      if (chartData) {
        setPendingClaimsGraphSeries([
          {
            ...pendingClaimsGraphSeries,
            data: getChartData(chartData, insuranceName),
          },
        ])
      }
      if (financialChartData) {
        setFinancialPendingClaimsGraphSeries([
          {
            ...financialPendingClaimsGraphSeries,
            data: getChartData(financialChartData, insuranceName),
          },
        ])
      }
      if (ageChartData) {
        setAgeGraphSeries([
          { ...ageGraphSeries, data: getAgeChartData(ageChartData) },
        ])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (loading)
    return (
      <div className='loadingContainer'>
        <CircularProgress />
      </div>
    )
  if (error) {
    return (
      <div>
        <GraphQlErrors error={error} />
      </div>
    )
  }
  const policyInsuranceTypeAggregate = get(
    insuranceDetails,
    'policyInsuranceTypeAggregate',
    []
  )
  const premiumClaimYearlyData = get(
    insuranceDetails,
    'riskInsurance.riskData.premiumClaimYearlyData',
    []
  )
  const financialPremiumClaimYearlyData = get(
    insuranceDetails,
    'riskInsurance.riskData.financialYearlyClaimData',
    []
  )
  const keySuggestions = get(
    insuranceDetails,
    'riskInsurance.riskData.keySuggestions',
    []
  )
  const insuranceSubTypeStats = get(
    insuranceDetails,
    'riskInsurance.riskData.insuranceSubTypeStats',
    []
  )
  const claimStatusCounts = get(
    insuranceDetails,
    'riskInsurance.riskData.claimStatusCounts',
    []
  )
  const financialClaimStatusCounts = get(
    insuranceDetails,
    'riskInsurance.riskData.financialYearClaimStatusCounts',
    []
  )
  const claimStatusUpdatedOn = get(
    insuranceDetails,
    'riskInsurance.riskData.claimStatusUpdatedOn',
    null
  )
  const financialClaimStatusUpdatedOn = get(
    insuranceDetails,
    'riskInsurance.riskData.financialYearClaimStatusUpdatedOn',
    null
  )
  const claimsTypeData = get(
    insuranceDetails,
    'riskInsurance.riskData.claimsTypeData',
    []
  )
  const financialClaimsTypeData = get(
    insuranceDetails,
    'riskInsurance.riskData.financialYearClaimsData',
    []
  )
  const claimsTypeDataEmployeeBenefits = get(
    insuranceDetails,
    'riskInsurance.riskData.claimsTypeData.employeeBenefits',
    []
  )
  const financialClaimsTypeDataEmployeeBenefits = get(
    insuranceDetails,
    'riskInsurance.riskData.financialYearClaimsData.employeeBenefits',
    []
  )
  const ageGraphData = get(
    insuranceDetails,
    'riskInsurance.riskData.ageDistribution',
    []
  )
  const policies = get(insuranceDetails, 'riskInsurance.policies', [])
  const aggregatedPolicies = get(
    insuranceDetails,
    'riskInsurance.aggregatedPolicies',
    {}
  )
  const reports = get(insuranceDetails, 'riskInsurance.riskData.reports', [])
  const addonCoverageData = get(
    insuranceDetails,
    'riskInsurance.riskData.addonCoverageData',
    []
  )
  const aggregates = get(insuranceDetails, 'policyInsuranceTypeAggregate', [])

  const policyTemplate = get(insuranceDetails, 'policyTemplate', {})
  const policyFeatures = get(insuranceDetails, 'policyTemplate.features', [])
  const keyPoints = get(
    insuranceDetails,
    'riskInsurance.riskData.keyPoints',
    null
  )
  const downloadReportData = get(
    insuranceDetails,
    'riskInsurance.riskData.groupedReports',
    {}
  )

  const getKeyPoints = () => {
    return !keyPoints ? policyTemplate?.keyPoints : keyPoints
  }

  const handleReportsDownload = () => {
    if (reports?.length === 0) return
    reports?.forEach((report) => {
      handleDocumentDownload([report], null)
    })
  }
  const handleDocumentDownload = async (document, documentURL) => {
    documentDownload(document ? document : [], documentURL)
  }

  const showContainer = (featureList) => {
    let isShowContainer = false
    if (featureList?.length) {
      featureList?.forEach((feature) => {
        if (policyFeatures?.includes(feature)) {
          isShowContainer = true
        }
        if (feature === TEMPLATE_FEATURES?.AGGREGATES) {
          isShowContainer = true
        }
      })
    }
    return isShowContainer
  }

  const getClaimsData = () => {
    if (insuranceName === INSURANCE_NAMES?.GROUP_TERM_INSURANCE) {
      return claimsTypeDataEmployeeBenefits
    }
    return claimsTypeData
  }

  const getFinancialClaimsData = () => {
    if (insuranceName === INSURANCE_NAMES?.GROUP_TERM_INSURANCE) {
      return financialClaimsTypeDataEmployeeBenefits
    }
    return financialClaimsTypeData
  }

  const getType = (insuranceType) => {
    if (insuranceType === INSURANCE_NAMES?.MARINE_INSURANCE) {
      return INSURANCE_CATEGORIES?.MARINE
    }
    return null
  }

  const getCategories = (name) => {
    switch (name) {
      case INSURANCE_NAMES?.MARINE_INSURANCE:
        return MARINE_INSURANCE_CATEGORIES
      case INSURANCE_NAMES?.FIRE_INSURANCE:
        return FIRE_INSURANCE_CATEGORIES
      default:
        return OTHER_INSURANCE_CATEGORIES
    }
  }

  return (
    <div>
      <div className={classes.root}>
        <div className={classes.backBtn}>
          <NavigateBackButton
            tooltipTitle='Back'
            title={`${insuranceName} Analysis`}
            handleClick={handleCloseDetails}
          />
        </div>
        <CloseButton
          id='closeButton'
          title='Close'
          handleClose={handleTotalCloseDetails}
          classname={classes.clsBtn}
        />
        <h2 className={classes.riskTitle}>{riskTitle}</h2>
        <div className={classes.contentDetails}>
          {!policyTemplate ? (
            <div className={classes.helperTextContainer}>
              <span className={classes.helperText}>
                There is no template for {insuranceName}
              </span>
            </div>
          ) : null}
          <Grid container spacing={3} alignItems='stretch'>
            {showContainer([
              TEMPLATE_FEATURES?.DOWNLOAD_REPORT,
              TEMPLATE_FEATURES?.PREMIUM_CLAIM_GRAPH,
              TEMPLATE_FEATURES?.CONTACT_US,
              TEMPLATE_FEATURES?.AGGREGATES,
            ]) && (
              <Grid item xs={12} sm={6} md={3}>
                {policyFeatures?.includes(
                  TEMPLATE_FEATURES?.DOWNLOAD_REPORT
                ) && (
                  <div
                    className={`${classes.cardRoot} ${classes.downloadReports}`}
                  >
                    <DownloadReports
                      title={`${insuranceName} Report(s)`}
                      text={DOWNLOAD_REPORTS_TEXT}
                      Icon={DOWNLOAD_DOCUMENT_ICONS?.[insuranceName]}
                      handleClick={handleReportsDownload}
                      downloadReportData={downloadReportData}
                    />
                  </div>
                )}
                <div className={classes.cardRoot}>
                  <Aggregate
                    premium={aggregates?.[0]?.premium}
                    sumAssured={aggregates?.[0]?.sumAssured}
                    vehicleIdv={aggregates?.[0]?.vehicleIdv}
                    insuranceType={insuranceName}
                  />
                </div>
                {policyFeatures?.includes(
                  TEMPLATE_FEATURES?.PREMIUM_CLAIM_GRAPH
                ) && (
                  <div className={classes.cardRoot}>
                    <PremiumAndClaimsView
                      series={pendingClaimsGraphSeries}
                      financialSeries={financialPendingClaimsGraphSeries}
                      categories={getCategories(insuranceName)}
                      data={premiumClaimYearlyData}
                      financialData={financialPremiumClaimYearlyData}
                      chartHeight={250}
                      type={getType(insuranceName)}
                    />
                  </div>
                )}
                {policyFeatures?.includes(TEMPLATE_FEATURES?.CONTACT_US) && (
                  <div className={classes.cardRoot}>
                    <CallToAction
                      text={CALL_TO_ACTION_TEXT}
                      options={[INSURANCE_DETAILS?.HELP_MOBILE]}
                    />
                  </div>
                )}
              </Grid>
            )}
            {showContainer([
              TEMPLATE_FEATURES?.KEY_SUGGESTIONS,
              TEMPLATE_FEATURES?.UPCOMING_RENEWALS,
            ]) && (
              <Grid item xs={12} sm={6} md={6}>
                {policyFeatures?.includes(
                  TEMPLATE_FEATURES?.KEY_SUGGESTIONS
                ) && (
                  <div className={classes.cardRoot}>
                    <div className={classes.keySuggestionsWrapper}>
                      <KeySuggestions
                        title={KEY_SUGGESTIONS}
                        data={keySuggestions}
                      />
                    </div>
                  </div>
                )}
                {policyFeatures?.includes(
                  TEMPLATE_FEATURES?.UPCOMING_RENEWALS
                ) && (
                  <div className={classes.cardRoot}>
                    <UpcomingRenewals
                      data={policies}
                      aggregatedPolicies={aggregatedPolicies}
                      ClientId={loggedInUserClientId}
                      insuranceName={insuranceName}
                    />
                  </div>
                )}
              </Grid>
            )}
            {showContainer([
              TEMPLATE_FEATURES?.KEY_DETAILS,
              TEMPLATE_FEATURES?.TOTAL_VEHICLES,
              TEMPLATE_FEATURES?.AGE_GRAPH,
              TEMPLATE_FEATURES?.TREND_ANALYSIS,
              TEMPLATE_FEATURES?.PENDING_CLAIMS,
              TEMPLATE_FEATURES?.COVERAGES,
            ]) && (
              <Grid item xs={12} sm={6} md={3}>
                {policyFeatures?.includes(TEMPLATE_FEATURES?.KEY_DETAILS) && (
                  <div className={classes.cardRoot}>
                    <div className={classes.keyDetailsWrapper}>
                      <DynamicKeyPoints keyPoints={getKeyPoints()} />
                    </div>
                  </div>
                )}
                {policyFeatures?.includes(
                  TEMPLATE_FEATURES?.TOTAL_VEHICLES
                ) && (
                  <div className={classes.cardRoot}>
                    <TotalVehicles
                      total={policies?.length}
                      otherData={policyInsuranceTypeAggregate}
                      chartData={insuranceSubTypeStats}
                    />
                  </div>
                )}
                {policyFeatures?.includes(TEMPLATE_FEATURES?.AGE_GRAPH) && (
                  <div className={classes.cardRoot}>
                    <AgeGraph
                      series={ageGraphSeries}
                      categories={CATEGORIES_AGE}
                      data={ageGraphData}
                      chartHeight={250}
                    />
                  </div>
                )}
                {policyFeatures?.includes(
                  TEMPLATE_FEATURES?.TREND_ANALYSIS
                ) && (
                  <div className={classes.cardRoot}>
                    <TrendAnalysis
                      title={TREND_ANALYSIS_TEXT}
                      data={premiumClaimYearlyData}
                    />
                  </div>
                )}
                {policyFeatures?.includes(TEMPLATE_FEATURES?.COVERAGES) && (
                  <div className={classes.cardRoot}>
                    <Coverages addonCoverageData={addonCoverageData} />
                  </div>
                )}
                {policyFeatures?.includes(
                  TEMPLATE_FEATURES?.PENDING_CLAIMS
                ) && (
                  <div className={classes.cardRoot}>
                    <PendingClaims
                      claimCounts={claimStatusCounts}
                      financialClaimCounts={financialClaimStatusCounts}
                      claimsData={getClaimsData()}
                      financialClaimData={getFinancialClaimsData()}
                      type={PENDING_CLAIM_TYPES?.[insuranceName]}
                      showDetailedView={true}
                      date={claimStatusUpdatedOn}
                      financialDate={financialClaimStatusUpdatedOn}
                    />
                  </div>
                )}
              </Grid>
            )}
          </Grid>
        </div>
      </div>
    </div>
  )
}

export default DynamicInsuranceTemplate

const useStyles = makeStyles({
  root: {
    background: COLORS.WHITE,
    position: 'relative',
    height: '100%',
  },
  riskTitle: {
    fontSize: '22px',
    marginTop: 0,
    fontWeight: 600,
  },
  contentDetails: {
    paddingTop: '15px',
  },
  cardRoot: {
    border: '1px solid #BBBBBB',
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: '24px',
  },
  backBtn: {
    position: 'absolute',
    left: '0px',
    top: '-3px',
  },
  clsBtn: {
    position: 'absolute',
    right: '-10px',
  },
  downloadReports: {
    padding: 0,
  },
  keySuggestionsWrapper: {
    maxHeight: '470px',
    overflow: 'auto',
  },
  keyDetailsWrapper: {
    height: '100%',
  },
  helperTextContainer: {
    border: '1px solid rgba(0,0,0,.125)',
    height: '100px',
    padding: '5px',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  helperText: {
    opacity: '0.6',
    fontSize: '20px',
    fontWeight: 'bold',
  },
  addTemplateButton: {
    whiteSpace: 'nowrap',
    textTransform: 'unset',
    fontSize: '13px',
    width: '120px',
    fontWeight: '600',
    background: COLORS.DARK_PINK,
    color: COLORS.WHITE,
    height: '24px',
    padding: '17px 15px !important',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: COLORS.DARK_PINK,
    },
    actionButton: {
      border: 'none',
    },
    tableStyle: {
      border: ' 3px solid gray',
    },
    tableHead: {
      backgroundColor: 'lightgray',
    },
  },
  buttonDiv: {
    marginTop: '10px',
  },
  modalRoot: {
    height: '90%',
  },
  modalContent: {
    textAlign: 'center',
    padding: '24px',
    height: 'calc(100% - 48px)',
    overflow: 'auto',
  },
})
